import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { getDomainOrigin } from 'src/common/utils/serverConfig';
import {
  getUserDetailsAPI,
  TravelFeBaseUrl,
  TravelFePdpUrl,
  TravelPassBaseUrl,
  VasFeBaseUrl,
} from 'src/common/utils/api/urls';
import { getRequest } from 'src/common/utils/api/request';
import isMobile from 'ismobilejs';
import {
  sendSegmentEvent,
  sendTravelPassEvents,
  sendVasEvents,
} from 'src/common/utils/segment';
import { EventType } from 'src/common/utils/segment/types';
import { useUser } from 'src/common/context-providers/user-provider';

export const useWidth = () => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    setWidth(window?.outerWidth > 0 ? window?.outerWidth : screen?.width);
  }, []);

  return width;
};

export const useHeight = () => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    setHeight(window?.outerHeight > 0 ? window?.outerHeight : screen?.height);
  }, []);

  return height;
};

export const useCurrentState = () => {
  const router = useRouter();
  const routes = router?.route?.split('/');
  const length = routes?.length;
  const currentState = length > 0 ? routes[length - 1] : '';

  return currentState;
};

export const useUserDetails = () => {
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      getUserDetails();
    }
  }, []);

  const getUserDetails = async () => {
    try {
      const res = await getRequest(`${getDomainOrigin()}${getUserDetailsAPI}`);
      if (res) setUserData(res?.data);
    } catch (error) {
      console.log({ error });
    }
  };

  return userData;
};

export const getUtmParams = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window?.location?.search);
    const isMobileDevice = isMobile();
    const isApp = urlParams?.get('platform')?.includes('app');
    const utm_medium = urlParams?.get('utm_medium');
    const utm_source = urlParams?.get('utm_source');
    const utm_campaign = urlParams?.get('utm_campaign');

    return {
      utm_medium:
        utm_medium || (isApp ? 'app' : isMobileDevice?.phone ? 'mweb' : 'web'),
      utm_source: utm_source || 'direct',
      utm_campaign: utm_campaign || undefined,
    };
  }
  return {};
};

export const useSegmentEvent = () => {
  const userDetails = useUser();

  const sendEvent = (
    type: string,
    eventName: string,
    payload,
    packageType = ''
  ) => {
    sendSegmentEvent(type, eventName, payload, userDetails, packageType);
  };

  return sendEvent;
};

export const useVasEvents = () => {
  const userDetails = useUser();

  const sendEvent = (eN: string, eT = EventType.VIEW, payload: any = {}) => {
    sendVasEvents(eN, eT, payload, userDetails);
  };

  return sendEvent;
};

export const useTravelPassEvents = () => {
  const userDetails = useUser();

  const sendEvent = (eN: string, payload: any = {}, eT = EventType.VIEW) => {
    sendTravelPassEvents(eN, payload, eT, userDetails);
  };

  return sendEvent;
};

// Router push hook for navigation
export const useRouterPush = (subRoot: string = TravelFeBaseUrl) => {
  const router = useRouter();
  const urlRoot = router?.query?.urlRoot ? `/${router?.query?.urlRoot}` : '';
  let baseUrl = `${urlRoot}${subRoot}`;

  if (subRoot === VasFeBaseUrl || subRoot === TravelPassBaseUrl)
    baseUrl = `${subRoot}`;

  const pushRoute = (route = '') => {
    router.push(`${baseUrl}${route}`);
  };

  return pushRoute;
};

// Returns Pdp URL
export const useTravelPdpRoute = () => {
  const router = useRouter();
  const urlRoot = router?.query?.urlRoot ? `/${router?.query?.urlRoot}` : '';
  const baseUrl = `${urlRoot}${TravelFePdpUrl}`;

  return `${baseUrl}`;
};

// Session and Local storage custom hooks

type StorageType = 'session' | 'local';

type UseStorageReturnValue = {
  getItem: (key: string, type?: StorageType) => string;
  setItem: (key: string, value: string, type?: StorageType) => boolean;
  removeItem: (key: string, type?: StorageType) => void;
};

const useStorage = (): UseStorageReturnValue => {
  const storageType = (type?: StorageType): 'localStorage' | 'sessionStorage' =>
    `${type ?? 'session'}Storage`;

  const isBrowser: boolean = ((): boolean => typeof window !== 'undefined')();

  const getItem = (key: string, type?: StorageType): string => {
    return isBrowser ? window[storageType(type)][key] : '';
  };

  const setItem = (key: string, value: string, type?: StorageType): boolean => {
    if (isBrowser) {
      window[storageType(type)].setItem(key, value);
      return true;
    }

    return false;
  };

  const removeItem = (key: string, type?: StorageType): void => {
    window[storageType(type)].removeItem(key);
  };

  return {
    getItem,
    setItem,
    removeItem,
  };
};

export default useStorage;

export const useElementOnScreen = (options) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const callBackFunction = (enteries) => {
    const [entry] = enteries;
    setIsVisible(entry?.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callBackFunction, options);
    const element = containerRef?.current;

    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [containerRef, options]);

  return [containerRef, isVisible];
};

export const useAtlCoupon = () => {
  const { getItem } = useStorage();
  const sessionObj = getItem('ATL_COUPON', 'session');
  const ATL_COUPON = sessionObj ? JSON.parse(sessionObj) : null;

  return ATL_COUPON;
};
